import React, {FC, useEffect, useState} from 'react';
import {Box, Button, Grid, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {ApplicationData, Question} from '@agenium-ai/agenium-client-sdk/dist/models';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import {firstValueFrom} from 'rxjs';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import FixedNavigation from './common/FixedNavigation';
import {useInstantApp} from './common/useInstantApp';
import {getUnderwritingDecision} from '@/Core/hooks/useUnderwritingDecision';

interface UnderwritingDisclosureProps {
    parameterStore: ParameterStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
    ageniumContext: AgenuimContextType;
    isPostDecision?: boolean;
}

const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#456d69',
        marginBottom: '28px',
    },
    formSection: {
        width: '100%',
        marginBottom: 24,
    },
    formSectionTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '30.72px',
        letterSpacing: '2%',
        color: '#434343',
        padding: '30px 0 20px',
    },
    label: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
        marginBottom: '16px',
    },
    formControl: {
        minWidth: 240,
        marginBottom: '20px',
    },
    navigation: {
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        padding: '20px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        flexWrap: 'nowrap',
    },
    backBtn: {
        borderRadius: '20px',
        minWidth: '145px',
    },
    nextBtn: {
        width: '100%',
        borderRadius: '20px',
    },
    btnWrapper: {
        width: '100%',
        minWidth: '200px',
        maxWidth: '400px',
    },
    optionButton: {
        paddingTop: 18,
        paddingBottom: 18,
        fontSize: 28,
        lineHeight: '150%',
        textTransform: 'none',
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        height: 80,
        minWidth: 220,
        color: '#174943',
        border: '1.75px solid',
        borderRadius: '8px',
        borderColor: '#456D69',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#F4F6FF',
        },
    },
    optionButtonActive: {
        backgroundColor: '#E8EBF8',
    },
}));

const UnderwritingDisclosure: FC<UnderwritingDisclosureProps> = observer(
    ({onNext, onBack, onExit, parameterStore, ageniumContext, isPostDecision = false}) => {
        const classes = useStyles();
        const {refer} = useInstantApp();
        const [disclosureValue, setDisclosureValue] = useState<'yes' | 'no' | ''>('');
        const [detailsText, setDetailsText] = useState('');
        const [concernFreeFormQuestion, setConcernFreeFormQuestion] = useState<Question>();
        const [gi1Question, setGi1Question] = useState<Question>();
        const [loading, setLoading] = useState(false);
        const [isManualUW, setIsManualUW] = useState(false);
        const [detailsTouched, setDetailsTouched] = useState(false);

        // Check if the application was previously referred to Manual UW
        useEffect(() => {
            const checkManualUWStatus = async () => {
                try {
                    const applicationData = ageniumContext.applicationData as ApplicationData[];

                    // Check if case has been referred to Manual UW previously
                    const hasManualUWDecision = applicationData?.some(
                        (data: ApplicationData) =>
                            data.key === 'Decision' &&
                            (data.value === 'MANUAL_UNDERWRITING' || data.value === 'Manual UW'),
                    );

                    if (hasManualUWDecision) {
                        setIsManualUW(true);
                        // Skip this screen if already in Manual UW
                        onNext();
                        return;
                    }
                } catch (error) {
                    console.error('Error checking Manual UW status:', error);
                }
            };

            checkManualUWStatus();
        }, [ageniumContext, onNext]);

        // Get the correct questions based on pre/post decision
        useEffect(() => {
            const getQuestions = async () => {
                try {
                    if (!ageniumContext.sdk) return;
                    const sections = await firstValueFrom(ageniumContext.sdk.getSections());

                    const gi1QuestionKey = isPostDecision
                        ? 'Case_AgentReport_GI1questionPostDecision'
                        : 'Case_AgentReport_GI1question';

                    const concernFreeFormKey = isPostDecision
                        ? 'Case_AgentReport_ConcernFreeForm2'
                        : 'Case_AgentReport_ConcernFreeForm';

                    let foundGi1Question: Question | undefined;
                    let foundConcernQuestion: Question | undefined;

                    // Search for questions in all sections
                    sections?.forEach((section: Section) => {
                        const searchQuestions = (questions: Question[]) => {
                            questions.forEach((q: Question) => {
                                if (q.key === gi1QuestionKey) {
                                    foundGi1Question = q;
                                    if (q.value === 'Yes') {
                                        setDisclosureValue('yes');
                                    } else if (q.value === 'No') {
                                        setDisclosureValue('no');
                                    }
                                } else if (q.key === concernFreeFormKey) {
                                    foundConcernQuestion = q;
                                    if (q.value) {
                                        setDetailsText(q.value as string);
                                        setDisclosureValue('yes');
                                    }
                                }

                                if (q.questions && q.questions.length > 0) {
                                    searchQuestions(q.questions);
                                }
                            });
                        };

                        if (section.questions) {
                            searchQuestions(section.questions);
                        }
                    });

                    setGi1Question(foundGi1Question);
                    setConcernFreeFormQuestion(foundConcernQuestion);

                    // Check if questions are already answered and skip if so
                    if (foundGi1Question && (foundGi1Question.value === 'Yes' || foundGi1Question.value === 'No')) {
                        console.log('GI1 question already answered, skipping UnderwritingDisclosure screen');
                        onNext();
                        return;
                    }

                    // Log errors if questions aren't found
                    if (!foundGi1Question) {
                        console.error(
                            'ERROR: Could not find Case_AgentReport_GI1questionPostDecision question in sections',
                        );
                        console.log(
                            'Available sections:',
                            sections?.map((s) => s.name),
                        );
                        sections?.forEach((section: Section) => {
                            console.log(
                                `Section ${section.name} questions:`,
                                section.questions?.map((q) => q.key),
                            );
                        });
                    }

                    if (!foundConcernQuestion) {
                        console.error('ERROR: Could not find Case_AgentReport_ConcernFreeForm2 question in sections');
                        if (foundGi1Question) {
                            console.log('GI1 question was found but ConcernFreeForm2 was not');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching questions:', error);
                }
            };

            if (!isManualUW) {
                getQuestions();
            }
        }, [ageniumContext, isPostDecision, isManualUW, onNext]);

        const handleSubmit = async () => {
            // Show validation if they haven't touched the field yet
            if (disclosureValue === 'yes' && !detailsText.trim()) {
                setDetailsTouched(true);
                return;
            }

            setLoading(true);
            try {
                if (!ageniumContext.sdk) {
                    console.error('ERROR: SDK not available for submission');
                    return;
                }

                console.log('Submitting underwriting disclosure:', {
                    disclosureValue,
                    detailsText: disclosureValue === 'yes' ? detailsText : '(not applicable)',
                    gi1QuestionFound: !!gi1Question,
                    concernQuestionFound: !!concernFreeFormQuestion,
                });

                // Update GI1 question
                if (gi1Question) {
                    gi1Question.value = disclosureValue === 'yes' ? 'Yes' : 'No';
                    console.log('Submitting GI1 question with value:', gi1Question.value);
                    try {
                        const gi1Result = await firstValueFrom(ageniumContext.sdk.submitAnswer(gi1Question));
                        console.log('✓ GI1 question submitted successfully:', gi1Result);
                    } catch (submitError) {
                        console.error('ERROR: Failed to submit GI1 question:', submitError);
                        throw submitError;
                    }
                } else {
                    console.error('ERROR: Cannot submit - GI1 question not found');
                }

                // Update concern free form question
                if (concernFreeFormQuestion) {
                    if (disclosureValue === 'yes') {
                        concernFreeFormQuestion.value = disclosureValue === 'yes' ? detailsText : '';
                        console.log('Submitting ConcernFreeForm2 with value:', concernFreeFormQuestion.value);
                        try {
                            const concernResult = await firstValueFrom(
                                ageniumContext.sdk.submitAnswer(concernFreeFormQuestion),
                            );
                            console.log('✓ ConcernFreeForm2 submitted successfully:', concernResult);
                        } catch (submitError) {
                            console.error('ERROR: Failed to submit ConcernFreeForm2:', submitError);
                            throw submitError;
                        }
                    }
                } else {
                    console.error('ERROR: Cannot submit concern text - ConcernFreeForm2 question not found');
                }

                console.log('✓✓ All underwriting disclosure submissions completed successfully');

                // Answering this post-decision disclosure can make Agenium return a new underwriting
                // decision (e.g. a refer). The AgeniumApplication decision watcher that normally routes
                // refer/decline is unmounted during the post-approval flow, so re-check the decision
                // here. refer() updates the Athena application status to REFERRED and routes to manual
                // underwriting; without it the application would remain APPROVED. Read straight from the
                // SDK so we see the freshest decision regardless of React state timing.
                const latestDecision = getUnderwritingDecision(ageniumContext.getApplicationData() ?? null);
                if (isPostDecision && latestDecision === 'REFERRED') {
                    refer();
                    return;
                }

                onNext();
            } catch (error) {
                console.error('Error submitting underwriting disclosure:', error);
                alert('Failed to submit underwriting disclosure. Please check the console for details.');
            } finally {
                setLoading(false);
            }
        };

        // If already in Manual UW, don't render anything
        if (isManualUW) {
            return null;
        }

        const isValid = disclosureValue === 'no' || (disclosureValue === 'yes' && detailsText.trim().length > 0);

        return (
            <div className={classes.wrapper}>
                <Typography className={classes.title}>Underwriting Disclosure</Typography>

                <Box className={classes.formSection}>
                    <Typography className={classes.formSectionTitle}>Disclosure</Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography className={classes.label}>
                                Do you know anything not disclosed which affects the underwriting of this risk?
                            </Typography>

                            <Grid container style={{marginBottom: 20, gap: 24}}>
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        classes={{
                                            root: `${classes.optionButton} ${disclosureValue === 'yes' ? classes.optionButtonActive : ''}`,
                                        }}
                                        onClick={() => {
                                            setDisclosureValue('yes');
                                        }}
                                    >
                                        Yes
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        classes={{
                                            root: `${classes.optionButton} ${disclosureValue === 'no' ? classes.optionButtonActive : ''}`,
                                        }}
                                        onClick={() => {
                                            setDisclosureValue('no');
                                            setDetailsText('');
                                            setDetailsTouched(false);
                                        }}
                                    >
                                        No
                                    </Button>
                                </Grid>
                            </Grid>

                            {disclosureValue === 'yes' && (
                                <>
                                    <Typography className={classes.label}>
                                        Provide details on what may affect underwriting
                                    </Typography>
                                    <InputText
                                        fullWidth
                                        multiline
                                        rows={6}
                                        value={detailsText}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setDetailsText(e.target.value)
                                        }
                                        onBlur={() => setDetailsTouched(true)}
                                        required
                                        error={
                                            detailsTouched &&
                                            disclosureValue === 'yes' &&
                                            detailsText.trim().length === 0
                                        }
                                        helperText={
                                            detailsTouched &&
                                            disclosureValue === 'yes' &&
                                            detailsText.trim().length === 0
                                                ? 'Please provide details about what may affect underwriting'
                                                : ''
                                        }
                                    />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
                <FixedNavigation
                    onBack={onBack}
                    isNextDisabled={!isValid || !disclosureValue || loading}
                    isLoading={loading}
                    onNext={handleSubmit}
                    onNextCustomText={loading ? 'Submitting...' : 'Next'}
                />
            </div>
        );
    },
);

export default UnderwritingDisclosure;
